import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import InfluencerVerificationLabel from 'components/influencer-verification-label/InfluencerVerificationLabel';

import InfluencerCreatorNetworkListContext from '../../context/InfluencerCreatorNetworkListContext';
import InfluencerCreatorNetworkListItemMetrics from '../../../../creator-modal/metrics/InfluencerCreatorNetworkListItemMetrics';
import InfluencerCreatorNetworkListPopover from '../../../../creator-modal/popover/InfluencerCreatorNetworkListPopover';

import { getCreatorNetworkInstagramUrl, getCreatorNetworkProfileUrl } from 'utils/influencers';

import './influencer-creator-network-list-connections.scss';

import type { NetworkGraphNodeRaw } from 'types';

type Props = {
  connections: Array<NetworkGraphNodeRaw>,
  influencerProfileId: number | undefined | null,
  parentInfluencerName: string,
  isLoading: boolean
};

const InfluencerCreatorNetworkListConnections: React.FunctionComponent<Props> = (
  {
    connections,
    influencerProfileId,
    parentInfluencerName,
    isLoading
  }: Props
) => {
  const context = React.useContext(InfluencerCreatorNetworkListContext);

  const isExpanded = !isLoading
    && !isEmpty(connections)
    && !isNil(context?.expandedId)
    && context?.expandedId === influencerProfileId;

  const containerClasses = classnames('influencer-creator-network-list-connections', {
    'influencer-creator-network-list-connections--is-expanded': isExpanded
  });

  if (!isExpanded || connections.length === 0) {
    return null;
  }

  return (
    <div className={containerClasses}>
      {
        isExpanded && (
          connections.map(item => {
            const profileUrl = getCreatorNetworkProfileUrl(item.profileId, item.profileName);
            const isClickable = !isNil(item.profileId);

            const linkClasses = classnames('influencer-creator-network-list-connections__link', {
              'influencer-creator-network-list-connections__link--is-clickable': isClickable
            });

            return (
              <div
                key={`influencer-creator-network-list-connections__item--${item.id}`}
                className="influencer-creator-network-list-connections__item"
              >
                <div className="influencer-creator-network-list-connections__bullet" />
                <div className="influencer-creator-network-list-connections__entity">
                  <InfluencerCreatorNetworkListPopover
                    channel="instagram"
                    identifier="connection-item__avatar-popover"
                    parentInfluencerName={parentInfluencerName}
                    type="follows"
                    isCollabaryProfile={!isNil(item.profileId)}
                    triggerComponent={(
                      <EntityAvatar
                        bordered
                        openInNewTab
                        entity={{
                          image: {
                            url: item.profileImage
                          }
                        }}
                        link={profileUrl}
                        customSize={{ width: 24, height: 24 }}
                      />
                    )}
                  />
                </div>
                <div className="influencer-creator-network-list-connections__name">
                  <InfluencerCreatorNetworkListPopover
                    channel="instagram"
                    identifier="connection-item__avatar-popover"
                    parentInfluencerName={parentInfluencerName}
                    type="follows"
                    isCollabaryProfile={!isNil(item.profileId)}
                    triggerComponent={(
                      <a
                        href={profileUrl}
                        className={linkClasses}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {item.profileName}
                      </a>
                    )}
                  />
                </div>
                <div className="influencer-creator-network-list-connections__verification-label">
                  {
                    !isNil(item.isLightweight) && (
                      <InfluencerVerificationLabel
                        type={item.isLightweight ? 'unverified' : 'verified'}
                      />
                    )
                  }
                </div>
                <InfluencerCreatorNetworkListItemMetrics
                  channel="instagram"
                  engagementRate={item.engagementRate}
                  followers={item.followers}
                  channelLink={getCreatorNetworkInstagramUrl(item.profileName)}
                  parentInfluencerName={parentInfluencerName}
                />
              </div>
            );
          })
        )
      }
    </div>
  );
};

export default InfluencerCreatorNetworkListConnections;
